import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [recoveryToken, setRecoveryToken] = useState<string>('');

  useEffect(() => {
    const run = async () => {
      logger.debug('[ResetPassword] Preparando sessão (manual)...');
      try {
        const hashRaw = window.location.hash?.startsWith('#')
          ? window.location.hash.slice(1)
          : window.location.hash;
        const searchRaw = window.location.search?.startsWith('?')
          ? window.location.search.slice(1)
          : window.location.search;

        const H = new URLSearchParams(hashRaw);
        const Q = new URLSearchParams(searchRaw);

        const type = H.get('type') || Q.get('type');
        const accessToken = H.get('access_token') || Q.get('access_token');
        const refreshToken = H.get('refresh_token') || Q.get('refresh_token');
        const code = H.get('code') || Q.get('code');
        const tokenHash = H.get('token_hash') || Q.get('token_hash') || H.get('token') || Q.get('token');
        const email = H.get('email') || Q.get('email');

        logger.debug('[ResetPassword] Params:', JSON.stringify({ type, hasAccessToken: !!accessToken, hasRefreshToken: !!refreshToken, hasCode: !!code, hasTokenHash: !!tokenHash, hasEmail: !!email }));

        const withTimeout = (p: Promise<any>) => Promise.race([
          p,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout ao estabelecer sessão')), 30000))
        ]);

        let result: any;
        if (code) {
          logger.debug('[ResetPassword] exchangeCodeForSession');
          result = await withTimeout(supabase.auth.exchangeCodeForSession(window.location.href));
        } else if (accessToken) {
          logger.debug('[ResetPassword] Tokens detectados, marcando como pronto');
          logger.debug('[ResetPassword] Access token length:', accessToken.length);
          logger.debug('[ResetPassword] Refresh token presente:', !!refreshToken);
          
          // Armazenar tokens manualmente no localStorage para o Supabase usar
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
          const projectRef = supabaseUrl.split('//')[1]?.split('.')[0] || 'ohhzgossedrpgtnmpfpt';
          const storageKey = `sb-${projectRef}-auth-token`;
          const authData = {
            access_token: accessToken,
            refresh_token: refreshToken || '',
            expires_in: 3600,
            expires_at: Math.floor(Date.now() / 1000) + 3600,
            token_type: 'bearer',
            user: null
          };
          
          try {
            localStorage.setItem(storageKey, JSON.stringify(authData));
            logger.debug('[ResetPassword] Tokens salvos no localStorage com chave:', storageKey);
          } catch (err) {
            logger.error('[ResetPassword] Erro ao salvar tokens:', err);
          }
          
          // Salvar token para usar depois
          setRecoveryToken(accessToken);
          
          // Marcar como pronto sem esperar setSession
          result = { data: { session: { access_token: accessToken } }, error: null };
        } else if (tokenHash) {
          logger.debug('[ResetPassword] verifyOtp com token_hash');
          if (email) {
            // Variante com email + token (alguns templates enviam ambos)
            result = await withTimeout(supabase.auth.verifyOtp({
              type: 'recovery',
              email,
              token: tokenHash
            }));
          } else {
            // Variante apenas com token_hash (nova API do supabase)
            result = await withTimeout(supabase.auth.verifyOtp({
              type: 'recovery',
              token_hash: tokenHash
            } as any));
          }
        } else {
          logger.warn('[ResetPassword] Parâmetros inválidos para recuperação');
          setTokenValid(false);
          setErrors({ general: 'Link inválido ou expirado. Solicite um novo link de recuperação.' });
          return;
        }

        // Verificar o resultado de forma adequada
        const sessionData = result?.data?.session || result?.session;
        const errorData = result?.error;

        if (errorData) {
          logger.error('[ResetPassword] Erro ao estabelecer sessão:', errorData);
          setTokenValid(false);
          setErrors({ general: errorData.message || 'Erro ao processar token. Solicite um novo link.' });
          return;
        }

        if (!sessionData) {
          logger.error('[ResetPassword] Nenhuma sessão retornada');
          setTokenValid(false);
          setErrors({ general: 'Erro ao estabelecer sessão. Tente novamente.' });
          return;
        }

        logger.success('[ResetPassword] Sessão pronta');
        setSessionReady(true);
        setTokenValid(true);

        // Limpar hash/query da URL para evitar reaproveitamento do token
        window.history.replaceState(null, '', window.location.pathname);
      } catch (err: any) {
        logger.error('[ResetPassword] Falha ao preparar sessão:', err);
        setTokenValid(false);
        setErrors({ general: err?.message || 'Erro ao processar token. Solicite um novo link.' });
      }
    };

    run();
  }, []);

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'A senha deve ter pelo menos 8 caracteres';
    }
    if (!hasUpperCase) {
      return 'A senha deve conter pelo menos uma letra maiúscula';
    }
    if (!hasLowerCase) {
      return 'A senha deve conter pelo menos uma letra minúscula';
    }
    if (!hasNumbers) {
      return 'A senha deve conter pelo menos um número';
    }
    if (!hasSpecialChar) {
      return 'A senha deve conter pelo menos um caractere especial';
    }
    return '';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug('[ResetPassword] Iniciando redefinição de senha...');
    
    setErrors({});
    setLoading(true);

    // Validações
    const newErrors: {[key: string]: string} = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'Nova senha é obrigatória';
    } else {
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      if (!sessionReady) {
        throw new Error('Sessão ainda não está pronta. Aguarde alguns segundos e tente novamente.');
      }

      logger.debug('[ResetPassword] Atualizando senha...');

      // Fazer requisição HTTP direta para evitar travamento do cliente Supabase
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      logger.debug('[ResetPassword] Usando token:', recoveryToken.substring(0, 20) + '...');
      
      const response = await fetch(`${supabaseUrl}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${recoveryToken}`
        },
        body: JSON.stringify({ password: formData.newPassword })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        logger.error('[ResetPassword] Erro ao atualizar senha:', errorData);
        throw new Error(errorData?.message || errorData?.msg || 'Erro ao atualizar senha');
      }
      
      logger.debug('[ResetPassword] Resposta da API:', await response.json().catch(() => 'sucesso'));

      logger.success('[ResetPassword] Senha redefinida com sucesso!');
      setSuccess(true);
      
      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error: any) {
      logger.error('[ResetPassword] Erro no processo:', error);
      setErrors({ general: error.message || 'Erro ao redefinir senha. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field: 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    const levels = [
      { text: 'Muito fraca', color: 'text-red-500' },
      { text: 'Fraca', color: 'text-orange-500' },
      { text: 'Regular', color: 'text-yellow-500' },
      { text: 'Boa', color: 'text-blue-500' },
      { text: 'Forte', color: 'text-green-500' }
    ];

    return { strength, ...levels[strength - 1] || levels[0] };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  // Se o token não é válido, mostrar mensagem de erro
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm bg-white rounded-card p-6 shadow-card border border-gray-100">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <i className="ri-error-warning-line text-red-600 text-3xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2 font-sans" style={{ fontWeight: 700 }}>
                Link Inválido ou Expirado
              </h2>
              <p className="text-gray-600 text-sm font-sans" style={{ fontWeight: 500 }}>
                Este link de recuperação é inválido ou já foi usado. Por favor, solicite um novo link de recuperação.
              </p>
            </div>
            <Button 
              onClick={() => navigate('/forgot-password')} 
              className="w-full mt-4"
            >
              <i className="ri-mail-send-line mr-2"></i>
              Solicitar Novo Link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Se foi com sucesso, mostrar mensagem de sucesso
  if (success) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm bg-white rounded-card p-6 shadow-card border border-gray-100">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <i className="ri-check-line text-green-600 text-3xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2 font-sans" style={{ fontWeight: 700 }}>
                Senha Redefinida!
              </h2>
              <p className="text-gray-600 text-sm font-sans" style={{ fontWeight: 500 }}>
                Sua senha foi alterada com sucesso. Redirecionando para o login...
              </p>
            </div>
            <div className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-green-600 text-2xl"></i>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-8">
      {/* Logo e Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-card flex items-center justify-center mb-4 mx-auto shadow-card overflow-hidden">
          <img 
            src="/Imagens/logo 2.0.png"
            alt="Logo Magistral"
            className="w-full h-full object-contain"
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-800 font-sans" style={{ 
          fontWeight: 700,
          textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          Nova Senha
        </h1>
        <p className="text-gray-600 mt-2 font-sans" style={{ fontWeight: 500 }}>
          Crie uma senha forte para sua conta
        </p>
      </div>

      {/* Formulário */}
      <div className="w-full max-w-sm bg-white rounded-card p-6 shadow-card border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Indicador de preparação */}
          {!sessionReady && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center">
                <i className="ri-loader-4-line animate-spin text-blue-600 mr-2"></i>
                <p className="text-blue-800 text-sm">Preparando sessão...</p>
              </div>
            </div>
          )}

          {/* Erro geral */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Nova Senha */}
          <div className="space-y-4">
            <div className="relative">
              <Input
                label="Nova senha"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                placeholder="Digite sua nova senha"
                error={errors.newPassword}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                <i className={showPasswords.new ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            </div>

            {/* Indicador de força da senha */}
            {formData.newPassword && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Força da senha:</span>
                  <span className={`text-sm font-medium ${passwordStrength.color}`}>
                    {passwordStrength.text}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.strength === 1 ? 'bg-red-500' :
                      passwordStrength.strength === 2 ? 'bg-orange-500' :
                      passwordStrength.strength === 3 ? 'bg-yellow-500' :
                      passwordStrength.strength === 4 ? 'bg-blue-500' :
                      'bg-green-500'
                    }`}
                    style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Requisitos da senha */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Requisitos da senha:</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className={`flex items-center ${formData.newPassword.length >= 8 ? 'text-green-600' : ''}`}>
                  <i className={`mr-2 ${formData.newPassword.length >= 8 ? 'ri-check-line' : 'ri-close-line'}`}></i>
                  Pelo menos 8 caracteres
                </li>
                <li className={`flex items-center ${/[A-Z]/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                  <i className={`mr-2 ${/[A-Z]/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                  Uma letra maiúscula
                </li>
                <li className={`flex items-center ${/[a-z]/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                  <i className={`mr-2 ${/[a-z]/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                  Uma letra minúscula
                </li>
                <li className={`flex items-center ${/\d/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                  <i className={`mr-2 ${/\d/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                  Um número
                </li>
                <li className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                  <i className={`mr-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                  Um caractere especial
                </li>
              </ul>
            </div>
          </div>

          {/* Confirmar Senha */}
          <div className="space-y-2">
            <div className="relative">
              <Input
                label="Confirmar nova senha"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Digite novamente sua nova senha"
                error={errors.confirmPassword}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                <i className={showPasswords.confirm ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            </div>
            
            {/* Indicador de confirmação */}
            {formData.confirmPassword && (
              <div className="flex items-center">
                {formData.newPassword === formData.confirmPassword ? (
                  <div className="flex items-center text-green-600">
                    <i className="ri-check-line mr-2"></i>
                    <span className="text-sm">Senhas coincidem</span>
                  </div>
                ) : (
                  <div className="flex items-center text-red-600">
                    <i className="ri-close-line mr-2"></i>
                    <span className="text-sm">Senhas não coincidem</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <Button type="submit" size="lg" className="w-full mt-6" disabled={loading || !sessionReady}>
            {loading ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Redefinindo...
              </>
            ) : !sessionReady ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Preparando...
              </>
            ) : (
              <>
                <i className="ri-key-line mr-2"></i>
                Redefinir Senha
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Mensagem Motivacional */}
      <div className="text-center mt-6">
        <p className="text-gray-500 text-sm font-sans" style={{ fontWeight: 500 }}>
          🔒 Escolha uma senha forte e única
        </p>
      </div>
    </div>
  );
}
